/* ---------------------------------------------------------------------------
   What the construction record says was built, day by day, on the model.

   THE SIMULATION IS A READING OF THE RECORD, NOT A PROGRAMME. Construction
   Scheduler holds what was planned; this is what the site said it did, laid onto
   the parts of the imported model so a day can be played back. Nothing here is
   inferred beyond what an entry states: a part no entry reaches stays a ghost to
   the end, because "nobody recorded building it" is the honest picture of it.

   HOW AN ENTRY FINDS ITS PARTS. Two things an entry carries, and neither is a
   component name the model shares:

     · the ACTIVITY says what kind of part it made — a pile concreted is a pile,
       a deck poured is a deck slab (RULES below). Where the activity is generic
       ("Concrete poured"), the entry's ELEMENT narrows it: poured at a pier is a
       column, or a bent cap if the component says cap.

     · the COMPONENT text says where — "Pier P1", "Abutment A2", "Span 3". It is
       free text in the site's own words, so only those three shapes are read.

   UNLESS THE ENTRY NAMES THE PART ITSELF. An entry recorded against a model
   with component ids carries the id of the part picked (LC-14), and that part is
   the one it lights — "Pile-3" at P2, not every Pile-3 on the bridge. Only an
   entry with no id, or an id this model does not have, falls back to reading
   the activity and the place as below.

   AND WHERE IS MEASURED, NOT NAMED. A CSiBridge export's part labels repeat —
   every pile group has its own "Pile-1" — so "pile 1 at pier 1" cannot be looked
   up. Each part's position along the bridge is measured off its geometry
   (src/modeler/meshView.ts does the measuring, this file only the arithmetic),
   the supports are found as clusters of substructure, and a part belongs to the
   nearest support or to the span it lies in.

   SEVERAL ENTRIES FOR ONE THING SHARE IT OUT. Piles bored at a pier over three
   days are three entries against the same eight piles; each takes its share in
   date order, across the width of the bridge, so the piling visibly advances.
   --------------------------------------------------------------------------- */

import type { ProgressEntry } from './types.ts';
import type { BaselineActivity } from './variance.ts';

/** The kinds of part the record's activities can build. */
export type PartKind =
  | 'pile'
  | 'pilecap'
  | 'footing'
  | 'abutment'
  | 'column'
  | 'bentcap'
  | 'bearing'
  | 'approach'
  | 'girder'
  | 'diaphragm'
  | 'deck'
  | 'barrier'
  | 'walkway'
  | 'surfacing'
  | 'other';

/** One part of the model, as measured. Stations run along the bridge, in metres. */
export interface PartInfo {
  sourceType: string;
  label: string;
  /** The model's component id, where the export gave one. LC-14. */
  id?: string | null;
  /** Centre, and extent, along the bridge's long axis. */
  station: number;
  min: number;
  max: number;
  /** Centre across the bridge — what shares a pier's piles out cap by cap. */
  lateral: number;
}

const SUBSTRUCTURE: ReadonlySet<PartKind> = new Set([
  'pile',
  'pilecap',
  'footing',
  'abutment',
  'column',
  'bentcap',
  'bearing',
  'approach',
]);

/** What kind of part this is, off the export's object type and label. */
export function partKind(sourceType: string, label: string): PartKind {
  /* Before the type: an export can carry the approach slab under its abutment or its
     foundation, and either would build it with that support's first pour. */
  if (/approach\s*slab/i.test(`${sourceType} ${label}`)) return 'approach';
  switch (sourceType) {
    case 'Foundation':
      if (/^pile/i.test(label)) return 'pile';
      if (/^F-?\d/i.test(label)) return 'pilecap';
      return 'footing';
    case 'Abutment':
      /* The bearings under the girder ends are exported inside the abutment, and
         an abutment's piles and footing can be. */
      if (/^B[AB]-?\d/i.test(label)) return 'bearing';
      if (/^pile/i.test(label)) return 'pile';
      if (/^(?:foundation|footing|pile\s*cap)/i.test(label)) return 'footing';
      /* The abutment's own object, captioned with its name: in CSiBridge that is the
         approach slab, which hangs off the abutment's main parameters. */
      if (/^abutment[-\s]?\d+$/i.test(label.trim())) return 'approach';
      return 'abutment';
    case 'PierBent':
      return 'bearing';
    case 'Column':
      return 'column';
    case 'BentCap':
      return 'bentcap';
    case 'PSCGirder':
    case 'SteelGirder':
    case 'Girder':
      return 'girder';
    case 'Diaphragm':
      return 'diaphragm';
    case 'DeckSlab':
      return 'deck';
    case 'Barrier':
      return 'barrier';
    case 'WalkWay':
      return 'walkway';
    case 'TrafficLane':
      return 'surfacing';
    default:
      return 'other';
  }
}

/* --------------------------- where --------------------------- */

/**
 * The supports' stations, first abutment to last: [A1, P1 … Pn, A2].
 *
 * Piers are clusters of columns (or of bent caps, or pile caps, where an export
 * has no columns); the abutments are the two ends of the abutment parts. A gap
 * of more than a twentieth of the bridge starts a new cluster, which separates
 * piers a span apart and keeps a pier's two columns together.
 */
export function supportStations(parts: readonly PartInfo[]): number[] {
  const kinds = parts.map((p) => partKind(p.sourceType, p.label));
  const all = parts.map((p) => p.station);
  if (all.length === 0) return [];
  const lo = Math.min(...parts.map((p) => p.min));
  const hi = Math.max(...parts.map((p) => p.max));
  const gap = Math.max(1, (hi - lo) / 20);

  const cluster = (xs: number[]): number[] => {
    const sorted = [...xs].sort((a, b) => a - b);
    const groups: number[][] = [];
    for (const x of sorted) {
      const last = groups[groups.length - 1];
      if (last && x - last[last.length - 1]! <= gap) last.push(x);
      else groups.push([x]);
    }
    return groups.map((g) => g.reduce((a, b) => a + b, 0) / g.length);
  };

  const of = (k: PartKind): number[] => parts.filter((_, i) => kinds[i] === k).map((p) => p.station);
  const abut = of('abutment');
  const mid = (lo + hi) / 2;
  const ends = [
    abut.filter((x) => x < mid),
    abut.filter((x) => x >= mid),
  ].map((xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null));
  const a1 = ends[0] ?? lo;
  const a2 = ends[1] ?? hi;

  const pierSource = [of('column'), of('bentcap'), of('pilecap')].find((xs) => xs.length > 0) ?? [];
  const piers = cluster(pierSource).filter((x) => x - a1 > gap && a2 - x > gap);
  return [a1, ...piers, a2];
}

/** Where a place is: a support by index, or a span by index — both from 0. */
export type Place = { support: number } | { span: number } | null;

/**
 * Where an entry's component text says it was.
 *
 * "Abutment A1", "A2", "Pier P1", "P2", "Pier 3", "Span 2", "Span-3". Anything
 * else — "All supports", a free description — is no place, and an entry with no
 * place reaches every part of its kind.
 */
export function placeOf(component: string | null, supports: number): Place {
  if (!component || supports < 2) return null;
  const abut = /\bAbutment\s*A?\s*-?\s*(\d+)|\bA(\d+)\b/i.exec(component);
  if (abut) {
    const n = Number(abut[1] ?? abut[2]);
    return { support: n <= 1 ? 0 : supports - 1 };
  }
  const pier = /\bPier\s*P?\s*-?\s*(\d+)|\bP(\d+)\b/i.exec(component);
  if (pier) {
    const n = Number(pier[1] ?? pier[2]);
    return n >= 1 && n <= supports - 2 ? { support: n } : null;
  }
  const span = /\bSpan\s*-?\s*(\d+)|\bS(\d+)\b/i.exec(component);
  if (span) {
    const n = Number(span[1] ?? span[2]);
    return n >= 1 && n <= supports - 1 ? { span: n - 1 } : null;
  }
  return null;
}

/** Which support a substructure part stands at, or which spans a superstructure part lies over. */
function partPlaces(p: PartInfo, kind: PartKind, stations: readonly number[]): { support: number | null; spans: number[] } {
  if (stations.length < 2) return { support: null, spans: [] };
  if (SUBSTRUCTURE.has(kind)) {
    let best = 0;
    for (let i = 1; i < stations.length; i++) {
      if (Math.abs(stations[i]! - p.station) < Math.abs(stations[best]! - p.station)) best = i;
    }
    return { support: best, spans: [] };
  }
  /* Over a span if it reaches more than a metre into it — a continuous deck or a
     full-length lane is over every span it crosses. */
  const spans: number[] = [];
  for (let i = 0; i + 1 < stations.length; i++) {
    const from = Math.max(p.min, stations[i]!);
    const to = Math.min(p.max, stations[i + 1]!);
    if (to - from > 1) spans.push(i);
  }
  if (spans.length === 0) {
    /* Shorter than a metre or wholly outside the supports: the nearest span. */
    let best = 0;
    for (let i = 0; i + 1 < stations.length; i++) {
      const c = (stations[i]! + stations[i + 1]!) / 2;
      const b = (stations[best]! + stations[best + 1]!) / 2;
      if (Math.abs(c - p.station) < Math.abs(b - p.station)) best = i;
    }
    spans.push(best);
  }
  return { support: null, spans };
}

/* --------------------------- what --------------------------- */

type Stage = 'progress' | 'done';

interface Rule {
  kinds: readonly PartKind[] | ((e: ProgressEntry) => readonly PartKind[]);
  stage: Stage;
}

/** The part an element-level activity (rebar, formwork, a pour) worked on. */
function byElement(e: ProgressEntry): readonly PartKind[] {
  const said = e.component ?? '';
  switch (e.element) {
    case 'abutment':
      /* An abutment's pile cap is exported as its Foundation, which reads as a footing. */
      return /footing|pile\s*cap/i.test(said) ? ['footing', 'pilecap'] : ['abutment'];
    case 'pier':
      if (/pile\s*cap/i.test(said)) return ['pilecap'];
      return /\bcap\b/i.test(said) ? ['bentcap'] : ['column'];
    case 'deck':
      return /diaphragm/i.test(said) ? ['diaphragm'] : ['deck'];
    case 'parapet':
      return ['barrier'];
    case 'bearings':
      return ['bearing'];
    case 'approach':
      return /slab/i.test(said) ? ['approach'] : [];
    default:
      return [];
  }
}

/**
 * Which activities build which parts, and whether the part is then finished or
 * only under way. Activities absent here make nothing on the model — a survey, a
 * hold point, a stoppage, a girder cast in the yard — and are still shown for the
 * day they happened.
 */
const RULES: Readonly<Record<string, Rule>> = {
  'pile-bored': { kinds: ['pile'], stage: 'progress' },
  'pile-cage': { kinds: ['pile'], stage: 'progress' },
  'pile-concreted': { kinds: ['pile'], stage: 'done' },
  /* A footing too: an abutment's pile cap is exported as its Foundation. */
  'pilecap-poured': { kinds: ['pilecap', 'footing'], stage: 'done' },
  'footing-poured': { kinds: ['footing', 'pilecap'], stage: 'done' },
  'rebar-fixed': { kinds: byElement, stage: 'progress' },
  'formwork-erected': { kinds: byElement, stage: 'progress' },
  'concrete-poured': { kinds: byElement, stage: 'done' },
  'bearing-set': { kinds: ['bearing'], stage: 'done' },
  'girder-erected': { kinds: ['girder'], stage: 'done' },
  'diaphragm-cast': { kinds: ['diaphragm'], stage: 'done' },
  'deck-poured': { kinds: ['deck'], stage: 'done' },
  'parapet-built': { kinds: ['barrier', 'walkway'], stage: 'done' },
  'wearing-course': { kinds: ['surfacing'], stage: 'done' },
};

/**
 * The parts of an abutment an entry can name, because an abutment is built in
 * lifts days apart — footing, front wall, bearing beam, wing walls, and the back
 * wall only once the girders are on — and "Abutment A1" alone would build all of
 * it with the first pour. The words are the site's; the labels are the export's.
 */
const SUBPARTS: readonly { said: RegExp; label: RegExp }[] = [
  { said: /front wall/i, label: /front wall/i },
  { said: /back wall/i, label: /back wall/i },
  { said: /wing/i, label: /wing/i },
  { said: /bearing beam/i, label: /bearing beam/i },
];

/**
 * The entries the playback plays: the accepted ones, and only those that have
 * reached the register. The same set the variance counts (`countable` in
 * src/screens/programme.ts), so the model never shows as built what the
 * programme does not count as done. LC-52.
 */
export function playedEntries<T extends Pick<ProgressEntry, 'state' | 'pending'>>(entries: readonly T[]): T[] {
  return entries.filter((e) => e.state === 'accepted' && !e.pending);
}

/** Whether an activity puts anything on the model. */
export function buildsPart(activity: string): boolean {
  return activity in RULES;
}

/** Words that put an entry off the structure even where it names a span: "Span 1 girders (yard)". */
const OFF_SITE = /\byard\b|\bdetour\b|\btemporary\b|\bold bridge\b/i;

/**
 * Whether an entry was recorded against something that is not this structure —
 * the casting yard, the temporary detour, the old bridge coming out.
 *
 * Said by the place in words naming somewhere off it (OFF_SITE), or by the entry
 * having neither an element nor a picked part, and a place in words that is not
 * a support, a span or a lift of one. Without this a detour's
 * panel bridge "Girder erected" would put every girder on the model in week four.
 * "All supports" still reaches everything where the entry has an element.
 */
function offStructure(e: ProgressEntry, place: Place): boolean {
  if (OFF_SITE.test(e.component ?? '')) return true;
  if (e.element || e.componentId || !e.component || place !== null) return false;
  return !SUBPARTS.some((s) => s.said.test(e.component!));
}

/**
 * The parts an entry is about, whether or not its activity builds them — a
 * cure, a survey or a hold point is still at a place on the structure.
 *
 * The part it names by id where the model has that id once; otherwise the
 * parts of the kind its activity (or, failing that, its element) says, at the
 * place its component says, narrowed to the lift it names. An entry with no
 * kind and no place is about nothing on the model.
 */
export function partsOf(parts: readonly PartInfo[], e: ProgressEntry): number[] {
  if (e.componentId) {
    const hits = parts.flatMap((p, i) => (p.id === e.componentId ? [i] : []));
    if (hits.length === 1) return hits;
  }
  const stations = supportStations(parts);
  const kinds = parts.map((p) => partKind(p.sourceType, p.label));
  const rule = RULES[e.activity];
  const want = rule ? (typeof rule.kinds === 'function' ? rule.kinds(e) : rule.kinds) : byElement(e);
  const place = placeOf(e.component, stations.length);
  if ((want.length === 0 && place === null) || offStructure(e, place)) return [];
  let found = parts
    .map((_, i) => i)
    .filter((i) => {
      if (want.length > 0 && !want.includes(kinds[i]!)) return false;
      if (place === null) return true;
      const at = partPlaces(parts[i]!, kinds[i]!, stations);
      return 'support' in place ? at.support === place.support : at.spans.includes(place.span);
    });
  const sub = SUBPARTS.find((s) => s.said.test(e.component ?? ''));
  if (sub) {
    const named = found.filter((i) => sub.label.test(parts[i]!.label));
    if (named.length > 0) found = named;
  }
  return found;
}

/**
 * The parts standing in formwork at the end of a day.
 *
 * From the day forms are recorded erected on a part until they are recorded
 * struck — or, where nobody recorded striking them, until the part's cure is
 * recorded complete, by which time the forms are off. Read off the entries the
 * site wrote, verified or not, in date order.
 */
export function formworkOn(parts: readonly PartInfo[], entries: readonly ProgressEntry[], day: string): Set<number> {
  const standing = new Set<number>();
  const upTo = entries
    .filter((e) => e.occurredOn <= day)
    .sort((a, b) => a.occurredOn.localeCompare(b.occurredOn) || a.createdAt.localeCompare(b.createdAt));
  for (const e of upTo) {
    if (e.activity === 'formwork-erected') for (const i of partsOf(parts, e)) standing.add(i);
    else if (e.activity === 'formwork-struck' || e.activity === 'concrete-cured') for (const i of partsOf(parts, e)) standing.delete(i);
  }
  return standing;
}

/* --------------------------- the plan --------------------------- */

export interface BuildPlan {
  /** Every calendar day from the first entry to the last, ISO. */
  days: string[];
  /** Per part, the day index it was started on, or null. */
  started: (number | null)[];
  /** Per part, the day index it was finished on, or null. */
  done: (number | null)[];
  /** The entries recorded on each day, in the order they were written. */
  entriesByDay: ProgressEntry[][];
  /** Entries whose activity builds a part but that found none on the model. */
  unplaced: ProgressEntry[];
  /** Per entry id, the parts that entry built or worked on. */
  claims: Map<string, number[]>;
  /** The excavations dug at the supports. See `Pit`. */
  pits: Pit[];
  /** Per day, the parts standing in formwork at its end. */
  forms: number[][];
}

export type PartState = 'none' | 'progress' | 'done';

/** One part's state at the end of a day. */
export function stateOn(plan: Pick<BuildPlan, 'started' | 'done'>, part: number, day: number): PartState {
  const d = plan.done[part];
  if (d !== null && d !== undefined && d <= day) return 'done';
  const s = plan.started[part];
  if (s !== null && s !== undefined && s <= day) return 'progress';
  return 'none';
}

function dayIndex(from: string, iso: string): number {
  return Math.round((Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

function addDay(iso: string, n: number): string {
  const at = new Date(`${iso}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + n);
  return at.toISOString().slice(0, 10);
}

/**
 * Lay a structure's construction record onto its parts.
 *
 * Entries are taken oldest first. Each that builds something claims the parts of
 * its kind at its place; where several entries share one activity, kind and
 * place, the parts are dealt out between them in date order, sorted across and
 * then along the bridge. A part only moves forward — once finished, a later
 * "under way" entry against it does not un-build it.
 */
export function planBuild(parts: readonly PartInfo[], entries: readonly ProgressEntry[]): BuildPlan {
  const sorted = [...entries]
    .filter((e) => /^\d{4}-\d{2}-\d{2}$/.test(e.occurredOn))
    .sort((a, b) => a.occurredOn.localeCompare(b.occurredOn) || a.createdAt.localeCompare(b.createdAt));
  const empty: BuildPlan = {
    days: [],
    started: parts.map(() => null),
    done: parts.map(() => null),
    entriesByDay: [],
    unplaced: [],
    claims: new Map(),
    pits: [],
    forms: [],
  };
  if (sorted.length === 0) return empty;

  const first = sorted[0]!.occurredOn;
  const last = sorted[sorted.length - 1]!.occurredOn;
  const span = dayIndex(first, last);
  const days = Array.from({ length: span + 1 }, (_, i) => addDay(first, i));
  const entriesByDay: ProgressEntry[][] = days.map(() => []);
  for (const e of sorted) entriesByDay[dayIndex(first, e.occurredOn)]!.push(e);

  const stations = supportStations(parts);
  const kinds = parts.map((p) => partKind(p.sourceType, p.label));
  const places = parts.map((p, i) => partPlaces(p, kinds[i]!, stations));

  /* A part by its id, for an entry that names one. An id two parts claim is no
     id, and the entry falls back to its place. */
  const byId = new Map<string, number>();
  const twice = new Set<string>();
  parts.forEach((p, i) => {
    if (!p.id) return;
    if (byId.has(p.id)) twice.add(p.id);
    byId.set(p.id, i);
  });
  for (const id of twice) byId.delete(id);

  const at = (i: number, place: Place): boolean => {
    if (place === null) return true;
    if ('support' in place) return places[i]!.support === place.support;
    return places[i]!.spans.includes(place.span);
  };

  /* The claims, grouped, so each group knows how many shares it is cut into. */
  interface Claim {
    entry: ProgressEntry;
    stage: Stage;
    candidates: number[];
  }
  const groups = new Map<string, Claim[]>();
  const unplaced: ProgressEntry[] = [];
  for (const e of sorted) {
    const rule = RULES[e.activity];
    if (!rule) continue;
    const named = e.componentId ? byId.get(e.componentId) : undefined;
    /* Named by id: the whole entry is that one part, and nothing is shared out —
       three days' boring of one pile are three entries against the same pile. */
    if (named !== undefined) {
      groups.set(`id#${groups.size}`, [{ entry: e, stage: rule.stage, candidates: [named] }]);
      continue;
    }
    const want = typeof rule.kinds === 'function' ? rule.kinds(e) : rule.kinds;
    const place = placeOf(e.component, stations.length);
    if (offStructure(e, place)) continue;
    let candidates = parts
      .map((_, i) => i)
      .filter((i) => want.includes(kinds[i]!) && at(i, place))
      .sort(
        (a, b) =>
          parts[a]!.lateral - parts[b]!.lateral || parts[a]!.station - parts[b]!.station,
      );
    /* Narrowed to the lift the entry names, where the model has one by that name. */
    const sub = SUBPARTS.find((s) => s.said.test(e.component ?? ''));
    if (sub) {
      const named = candidates.filter((i) => sub.label.test(parts[i]!.label));
      if (named.length > 0) candidates = named;
    }
    if (candidates.length === 0) {
      unplaced.push(e);
      continue;
    }
    /* Grouped by the parts claimed, not only by what was said: two entries share
       parts out only where they are after the same parts. */
    const key = `${e.activity}|${candidates.join(',')}`;
    const list = groups.get(key) ?? [];
    list.push({ entry: e, stage: rule.stage, candidates });
    groups.set(key, list);
  }

  const started: (number | null)[] = parts.map(() => null);
  const done: (number | null)[] = parts.map(() => null);
  const byEntry = new Map<string, number[]>();
  for (const claims of groups.values()) {
    claims.forEach((c, k) => {
      const n = c.candidates.length;
      const share = c.candidates.slice(Math.floor((k * n) / claims.length), Math.floor(((k + 1) * n) / claims.length));
      byEntry.set(c.entry.id, share);
      const day = dayIndex(first, c.entry.occurredOn);
      for (const i of share) {
        if (started[i] === null || day < started[i]!) started[i] = day;
        if (c.stage === 'done' && (done[i] === null || day < done[i]!)) done[i] = day;
      }
    });
  }
  /* Finished without anybody recording it started: it started the day it finished. */
  for (let i = 0; i < parts.length; i++) {
    if (done[i] !== null && (started[i] === null || started[i]! > done[i]!)) started[i] = done[i]!;
  }

  /* The forms, day by day: up on the parts the erecting entry was dealt, down on
     the parts a strike or a completed cure names — as `formworkOn` reads them. */
  const forms: number[][] = [];
  const standing = new Set<number>();
  for (const todays of entriesByDay) {
    for (const e of todays) {
      if (e.activity === 'formwork-erected') for (const i of byEntry.get(e.id) ?? partsOf(parts, e)) standing.add(i);
      else if (e.activity === 'formwork-struck' || e.activity === 'concrete-cured') for (const i of partsOf(parts, e)) standing.delete(i);
    }
    forms.push([...standing]);
  }

  /* The digging and the filling back, at the support each entry names. */
  const earth = new Map<number, Earth>();
  for (const e of sorted) {
    const dig = e.activity === 'excavation';
    const fill = e.activity === 'backfill';
    const place = placeOf(e.component, stations.length);
    if (!place || !('support' in place) || OFF_SITE.test(e.component ?? '')) continue;
    const at = earthAt(earth, place.support);
    if (COFFERDAM.test(`${e.component ?? ''} ${e.note}`)) at.cofferdam = true;
    if (!dig && !fill) continue;
    const day = dayIndex(first, e.occurredOn);
    (dig ? at.dig : at.fill).push({ from: day, to: day, amount: e.quantity ?? 0 });
  }
  const pits = pitsOf(earth, parts, kinds, places, days.length, done);

  return { days, started, done, entriesByDay, unplaced, claims: byEntry, pits, forms };
}

/**
 * The first day anything shows on the model: a part under way, a pit dug,
 * forms standing. The days before it — mobilisation, the detour, the yard — play
 * as an empty model, so the playback starts here. 0 where nothing ever shows.
 */
export function firstShown(plan: Pick<BuildPlan, 'started' | 'pits'> & { forms?: number[][] }): number {
  let first = Infinity;
  for (const s of plan.started) if (s !== null && s < first) first = s;
  for (const pit of plan.pits) {
    const d = pit.dug.findIndex((x) => x > 0);
    if (d >= 0 && d < first) first = d;
  }
  const f = plan.forms?.findIndex((x) => x.length > 0) ?? -1;
  if (f >= 0 && f < first) first = f;
  return Number.isFinite(first) ? first : 0;
}

/* --------------------------- the site's own works --------------------------- */

/*
   WHAT THE SITE DIGS THAT THE MODEL HAS NO PART FOR. A CSiBridge export is the
   finished structure; the hole its footing is poured in is not in it. A support
   the record (or the programme) says was excavated gets a pit, sized off the
   foundation measured at that support and dug as deep, day by day, as the
   quantities say so far — or, on the programme, as far through its planned days.

   FILLED BACK ONLY ROUND A FOUNDATION THAT IS THERE. Backfill at a support counts
   against its pit once everything the pit was dug for is poured; before that it
   is a pocket or a blinding made good under the footing, and the pit stays open.
   A pit nobody records filling stays open to the end, as the record has it.
*/

/** One excavation at a support. */
export interface Pit {
  /** The support it is dug at, from 0: [A1, P1 … Pn, A2]. */
  support: number;
  /** The parts it is dug for: the foundation at the support, or what stands there where the model has none. */
  around: number[];
  /** Sheet piles or sandbags round it, said by an entry or by the programme. */
  cofferdam: boolean;
  /**
   * The day the cofferdam comes out: the day what stands on the foundation is
   * out of the water — the front wall at an abutment, the columns at a pier.
   * Nobody records sheets pulled, so this is the playback's rule. Null while it stands.
   */
  pulled: number | null;
  /** Per day of the plan, how far it is dug and how far filled back, each 0–1. */
  dug: number[];
  filled: number[];
}

const COFFERDAM = /cofferdam|sheet\s*pil/i;

/** A stretch of digging or filling: days from–to, and how much, where said. */
interface Earthwork {
  from: number;
  to: number;
  amount: number;
}

interface Earth {
  dig: Earthwork[];
  fill: Earthwork[];
  cofferdam: boolean;
}

function earthAt(earth: Map<number, Earth>, support: number): Earth {
  let at = earth.get(support);
  if (!at) earth.set(support, (at = { dig: [], fill: [], cofferdam: false }));
  return at;
}

/**
 * How far through the work is at the end of each day, 0–1. Each stretch moves
 * evenly across its days; by quantity where every stretch gives one, else by
 * days of work.
 */
function progressOf(works: readonly Earthwork[], days: number): number[] {
  const byQuantity = works.every((w) => w.amount > 0);
  const weight = (w: Earthwork): number => (byQuantity ? w.amount : w.to - w.from + 1);
  const total = works.reduce((s, w) => s + weight(w), 0);
  return Array.from({ length: days }, (_, d) => {
    if (total <= 0) return 0;
    let sum = 0;
    for (const w of works) sum += weight(w) * Math.max(0, Math.min(1, (d - w.from + 1) / (w.to - w.from + 1)));
    return sum / total;
  });
}

function pitsOf(
  earth: ReadonlyMap<number, Earth>,
  parts: readonly PartInfo[],
  kinds: readonly PartKind[],
  places: readonly { support: number | null }[],
  days: number,
  done: readonly (number | null)[],
): Pit[] {
  const pits: Pit[] = [];
  for (const [support, at] of [...earth].sort((a, b) => a[0] - b[0])) {
    if (at.dig.length === 0) continue;
    let around: number[] = [];
    for (const want of [['footing', 'pilecap'], ['pile'], ['abutment', 'column']] as PartKind[][]) {
      around = parts.flatMap((_, i) => (want.includes(kinds[i]!) && places[i]!.support === support ? [i] : []));
      if (around.length > 0) break;
    }
    if (around.length === 0) continue;
    const poured = around.reduce((m, i) => Math.max(m, done[i] ?? Infinity), -Infinity);
    /* What stands on the foundation: the front wall where the model names one, else the walls or columns there. */
    const standing = parts.flatMap((_, i) =>
      (kinds[i] === 'abutment' || kinds[i] === 'column') && places[i]!.support === support ? [i] : [],
    );
    const front = standing.filter((i) => SUBPARTS[0]!.label.test(parts[i]!.label));
    const up = (front.length > 0 ? front : standing).reduce((m, i) => Math.max(m, done[i] ?? Infinity), -Infinity);
    pits.push({
      support,
      around,
      cofferdam: at.cofferdam,
      pulled: Number.isFinite(up) ? up : null,
      dug: progressOf(at.dig, days),
      filled: progressOf(at.fill.filter((w) => w.to > poured), days),
    });
  }
  return pits;
}

/** Whether a programme activity digs at a support, fills one back, or neither. */
function earthworkOf(a: BaselineActivity): 'dig' | 'fill' | null {
  const said = `${a.kind} ${a.name} ${a.component ?? ''}`;
  if (/backfill/i.test(said)) return 'fill';
  if (/excavat|cofferdam/i.test(said) || /^earthworks?$/i.test(a.kind.trim())) return 'dig';
  return null;
}

/* --------------------------- the programme --------------------------- */

/*
   THE SCHEDULER'S PLAYBACK: THE PROGRAMME, NOT THE RECORD. Construction
   Scheduler's Simulate plays the accepted baseline onto the model — each part
   under way from its activity's planned start, built on its planned finish — so
   the sequence can be seen before anybody builds it. The same parts, places and
   lifts as the record's playback; what differs is where the dates come from.

   AN ACTIVITY FINDS ITS PARTS the way an entry does: by the component id the
   programme carries where it was derived from the model (src/data/importedProgramme.ts),
   otherwise by its kind ("Pile", "Pile Cap", "Wall" …) at the place its component
   or its group names ("Abutment A1 piles", under "Abutment A1"). An activity off
   the structure — the casting yard, the detour, the old bridge — finds none.
*/

/** The parts a programme activity kind builds. Kinds the modeller has that build nothing are absent. */
const PROGRAMME_KINDS: Readonly<Record<string, readonly PartKind[]>> = {
  pile: ['pile'],
  'pile cap': ['pilecap', 'footing'],
  foundation: ['footing', 'pilecap'],
  footing: ['footing', 'pilecap'],
  abutment: ['abutment'],
  wall: ['abutment'],
  'wing wall': ['abutment'],
  'back wall': ['abutment'],
  column: ['column'],
  pier: ['column'],
  'bent cap': ['bentcap'],
  'pier cap': ['bentcap'],
  bearing: ['bearing'],
  'approach slab': ['approach'],
  girder: ['girder'],
  diaphragm: ['diaphragm'],
  deck: ['deck'],
  'deck section': ['deck'],
  'deck slab': ['deck'],
  barrier: ['barrier', 'walkway'],
  'side wall': ['barrier', 'walkway'],
  parapet: ['barrier', 'walkway'],
  walkway: ['walkway'],
  'road surface': ['surfacing'],
};

/** Where a kind is not one of the modeller's, the activity's own words. */
const PROGRAMME_WORDS: readonly [RegExp, readonly PartKind[]][] = [
  [/pile\s*cap/i, ['pilecap', 'footing']],
  [/\bpile/i, ['pile']],
  [/footing|foundation/i, ['footing', 'pilecap']],
  [/front wall|back wall|wing wall|bearing beam/i, ['abutment']],
  [/column|pier shaft/i, ['column']],
  [/\bcap\b|crosshead|cross-head/i, ['bentcap']],
  [/bearing/i, ['bearing']],
  [/approach\s*slab/i, ['approach']],
  [/girder/i, ['girder']],
  [/diaphragm/i, ['diaphragm']],
  [/deck/i, ['deck']],
  [/barrier|parapet|footway|walkway/i, ['barrier', 'walkway']],
  [/wearing|surfacing|asphalt/i, ['surfacing']],
];

/** Words that put an activity off the structure, or make nothing on it: the yard, the detour, a test, a dig. */
const PROGRAMME_OFF = /\byard\b|\bdetour\b|\btemporary\b|\bold bridge\b|\btests?\b|\bexcavat|\bbackfill|\bremoval\b/i;

export interface ProgrammePlan {
  /** Every calendar day from the first planned start to the last planned finish of an activity that builds a part, ISO. */
  days: string[];
  started: (number | null)[];
  done: (number | null)[];
  /** The activities that build a part planned to be under way on each day, in programme order. */
  activeByDay: BaselineActivity[][];
  /** Per activity id, the parts it builds. Absent for one that builds nothing on the model. */
  claims: Map<string, number[]>;
  /** The excavations the programme digs at the supports. See `Pit`. */
  pits: Pit[];
}

/** The parts a programme activity builds. `context` is its group names, nearest first. */
export function programmeParts(
  parts: readonly PartInfo[],
  a: BaselineActivity,
  context: readonly string[],
  stations: readonly number[] = supportStations(parts),
  kinds: readonly PartKind[] = parts.map((p) => partKind(p.sourceType, p.label)),
): number[] {
  if (a.componentId) {
    const exact = parts.flatMap((p, i) => (p.id === a.componentId ? [i] : []));
    if (exact.length > 0) return exact;
    const under = parts.flatMap((p, i) => (p.id?.startsWith(`${a.componentId}/`) ? [i] : []));
    if (under.length > 0) return under;
  }
  const said = `${a.component ?? ''} ${a.name}`;
  /* "Backfill and approach slab" fills and builds: the slab is still poured. */
  if (PROGRAMME_OFF.test(said) && !/approach\s*slab/i.test(`${a.kind} ${said}`)) return [];
  const want =
    PROGRAMME_KINDS[a.kind.trim().toLowerCase()] ?? PROGRAMME_WORDS.find(([re]) => re.test(said))?.[1] ?? [];
  if (want.length === 0) return [];
  let place: Place = null;
  for (const text of [a.component ?? '', a.name, ...context]) {
    place = placeOf(text, stations.length);
    if (place !== null) break;
  }
  let found = parts
    .map((_, i) => i)
    .filter((i) => {
      if (!want.includes(kinds[i]!)) return false;
      if (place === null) return true;
      const at = partPlaces(parts[i]!, kinds[i]!, stations);
      return 'support' in place ? at.support === place.support : at.spans.includes(place.span);
    })
    .sort((x, y) => parts[x]!.lateral - parts[y]!.lateral || parts[x]!.station - parts[y]!.station);
  /* A lift the model has no part by the name of builds nothing, rather than the
     whole abutment on the day the bearing beam is poured. */
  const sub = SUBPARTS.find((s) => s.said.test(said));
  if (sub) found = found.filter((i) => sub.label.test(parts[i]!.label));
  return found;
}

/**
 * Lay an accepted programme onto the parts of the model.
 *
 * Each leaf activity's parts are dealt across its planned days, across and then
 * along the bridge, so eight piles over eight days go in one a day and a single
 * pour is under way from its start and built on its finish. A part two
 * activities build is under way from the first start and built at the first
 * finish — it only moves forward, as the record's does.
 *
 * ONLY WHAT SHOWS ON THE MODEL IS PLAYED. An activity that builds no part and
 * digs no pit — mobilisation, setting out, the detour, the yard, a test — is
 * left out of the days and the card alike, so the playback starts on the first
 * day something is dug or goes up and ends on the last.
 */
export function planProgramme(parts: readonly PartInfo[], activities: readonly BaselineActivity[]): ProgrammePlan {
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  const dated = activities.filter((a) => !a.isGroup && iso.test(a.plannedStart) && iso.test(a.plannedFinish));

  const byId = new Map(activities.map((a) => [a.activityId, a]));
  const contextOf = (a: BaselineActivity): string[] => {
    const out: string[] = [];
    for (let p = a.parentId ? byId.get(a.parentId) : undefined; p && out.length < 8; p = p.parentId ? byId.get(p.parentId) : undefined) {
      out.push(p.name);
    }
    return out;
  };

  const stations = supportStations(parts);
  const kinds = parts.map((p) => partKind(p.sourceType, p.label));
  const started: (number | null)[] = parts.map(() => null);
  const done: (number | null)[] = parts.map(() => null);
  const claims = new Map<string, number[]>();
  for (const a of dated) {
    const mine = programmeParts(parts, a, contextOf(a), stations, kinds);
    if (mine.length > 0) claims.set(a.activityId, mine);
  }
  /* The digging and filling back, at the support the activity or its group names. */
  const earthworks = new Map<string, { support: number; kind: 'dig' | 'fill' }>();
  for (const a of dated) {
    const kind = earthworkOf(a);
    const context = contextOf(a);
    if (!kind || OFF_SITE.test(`${a.component ?? ''} ${a.name} ${context.join(' ')}`)) continue;
    for (const text of [a.component ?? '', a.name, ...context]) {
      const place = placeOf(text, stations.length);
      if (place === null) continue;
      if ('support' in place) earthworks.set(a.activityId, { support: place.support, kind });
      break;
    }
  }
  const leaves = dated.filter((a) => claims.has(a.activityId) || earthworks.has(a.activityId));
  if (leaves.length === 0) return { days: [], started, done, activeByDay: [], claims, pits: [] };

  const first = leaves.reduce((m, a) => (a.plannedStart < m ? a.plannedStart : m), leaves[0]!.plannedStart);
  const last = leaves.reduce((m, a) => (a.plannedFinish > m ? a.plannedFinish : m), leaves[0]!.plannedFinish);
  const days = Array.from({ length: dayIndex(first, last) + 1 }, (_, i) => addDay(first, i));
  const activeByDay: BaselineActivity[][] = days.map(() => []);

  for (const a of leaves) {
    const from = dayIndex(first, a.plannedStart);
    const to = Math.max(from, dayIndex(first, a.plannedFinish));
    for (let d = from; d <= to; d++) activeByDay[d]!.push(a);
    const mine = claims.get(a.activityId);
    if (!mine) continue;
    const span = to - from + 1;
    const n = mine.length;
    mine.forEach((i, j) => {
      const s = from + Math.floor((j * span) / n);
      const f = Math.max(s, from + Math.ceil(((j + 1) * span) / n) - 1);
      if (started[i] === null || s < started[i]!) started[i] = s;
      if (done[i] === null || f < done[i]!) done[i] = f;
    });
  }
  const earth = new Map<number, Earth>();
  for (const a of leaves) {
    const work = earthworks.get(a.activityId);
    if (!work) continue;
    const at = earthAt(earth, work.support);
    if (COFFERDAM.test(`${a.name} ${a.component ?? ''}`)) at.cofferdam = true;
    const from = dayIndex(first, a.plannedStart);
    (work.kind === 'dig' ? at.dig : at.fill).push({ from, to: Math.max(from, dayIndex(first, a.plannedFinish)), amount: 0 });
  }
  const places = parts.map((p, i) => partPlaces(p, kinds[i]!, stations));
  return { days, started, done, activeByDay, claims, pits: pitsOf(earth, parts, kinds, places, days.length, done) };
}
